import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetPackageQuery, useCreatePackageMutation, useUpdatePackageMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';

interface FeatureRow {
  feature: string;
  feature_ar: string;
}

const AdminPackageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetPackageQuery(id!, { skip: !isEdit });
  const [createPackage, { isLoading: isCreating }] = useCreatePackageMutation();
  const [updatePackage, { isLoading: isUpdating }] = useUpdatePackageMutation();

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    price: '',
    period: '',
    period_type: 'months',
  });

  const [features, setFeatures] = useState<FeatureRow[]>([{ feature: '', feature_ar: '' }]);

  useEffect(() => {
    if (isEdit && data?.data) {
      const pkg = data.data;
      setFormData({
        name: pkg.name || '',
        name_ar: pkg.name_ar || '',
        price: pkg.price || '',
        period: pkg.period || '',
        period_type: pkg.period_type || 'months',
      });
      
      // Parse features
      let parsedFeatures: FeatureRow[] = [{ feature: '', feature_ar: '' }];
      if (pkg.features && pkg.features_ar) {
        const featuresList = typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features;
        const featuresArList = typeof pkg.features_ar === 'string' ? JSON.parse(pkg.features_ar) : pkg.features_ar;
        
        if (Array.isArray(featuresList) && Array.isArray(featuresArList)) {
          parsedFeatures = featuresList.map((feat: string, idx: number) => ({
            feature: feat || '',
            feature_ar: featuresArList[idx] || '',
          }));
        }
      }
      
      if (parsedFeatures.length === 0) {
        parsedFeatures = [{ feature: '', feature_ar: '' }];
      }
      setFeatures(parsedFeatures);
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (index: number, field: 'feature' | 'feature_ar', value: string) => {
    setFeatures(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFeatureRow = () => {
    setFeatures(prev => [...prev, { feature: '', feature_ar: '' }]);
  };

  const removeFeatureRow = (index: number) => {
    setFeatures(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const featuresList = features.map(f => f.feature).filter(Boolean);
      const featuresArList = features.map(f => f.feature_ar).filter(Boolean);

      const submitData = {
        name: formData.name,
        name_ar: formData.name_ar,
        price: parseFloat(formData.price),
        period: parseFloat(formData.period),
        period_type: formData.period_type,
        features: featuresList,
        features_ar: featuresArList,
        status: 1,
      };

      if (isEdit) {
        const result = await updatePackage({ id: parseInt(id!), ...submitData }).unwrap();
        showSuccess(result.message || 'Package updated successfully!');
      } else {
        const result = await createPackage(submitData).unwrap();
        showSuccess(result.message || 'Package created successfully!');
      }
      
      navigate('/admin/packages');
    } catch (err: any) {
      showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} package. Please try again.`);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const currency = process.env.REACT_APP_CURRENCY || '$';

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit Package' : 'Add New Package'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/packages'); }} className="text-muted text-hover-primary">Packages</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Package Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Package Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Package Name Arabic</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Package Name in Arabic"
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Time Period</label>
                      <div className="input-group mb-3">
                        <input
                          type="number"
                          required
                          className="form-control form-control-solid"
                          placeholder="Enter Time Period"
                          name="period"
                          value={formData.period}
                          onChange={handleInputChange}
                        />
                        <span className="input-group-text p-0">
                          <select
                            name="period_type"
                            className="form-select"
                            value={formData.period_type}
                            onChange={handleInputChange}
                          >
                            <option value="months">months</option>
                            <option value="days">days</option>
                          </select>
                        </span>
                      </div>
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Price</label>
                      <div className="input-group mb-3">
                        <input
                          type="number"
                          step="0.01"
                          required
                          className="form-control form-control-solid"
                          placeholder="Enter Package Price"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                        />
                        <span className="input-group-text">{currency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <button type="button" className="btn btn-base btn-sm mb-3" onClick={addFeatureRow}>
                    Add Features
                  </button>
                  
                  <div className="features" id="features">
                    {features.map((featureRow, index) => (
                      <div key={index} className="row mb-4">
                        <div className="col-md-5">
                          <input
                            type="text"
                            name={`features[]`}
                            placeholder="write feature here"
                            className="form-control"
                            value={featureRow.feature}
                            onChange={(e) => handleFeatureChange(index, 'feature', e.target.value)}
                          />
                        </div>
                        <div className="col-md-5">
                          <input
                            type="text"
                            name={`features_ar[]`}
                            placeholder="write feature in arabic"
                            className="form-control"
                            value={featureRow.feature_ar}
                            onChange={(e) => handleFeatureChange(index, 'feature_ar', e.target.value)}
                          />
                        </div>
                        <div className="col-md-2">
                          {features.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removeFeatureRow(index)}
                            >
                              - Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="text-center mt-5">
                    <button type="reset" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/packages')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageForm;
